import type { Project, Task, TaskWorkflowStateT, WorkflowColumn } from '@portfolio-pm/shared';
import { type FormEvent, useState } from 'react';
import { getDocStore } from '../../store/doc-store.ts';
import { ScrollViewport } from '../layout/ScrollViewport.tsx';
import { formatShortDate, formatTaskDate } from './task-view-dates.ts';
import { compareTasks, getTaskWorkflowStateLabel } from './task-workspace-model.ts';

const DRAG_MIME = 'application/x-portfolio-task';

export function BoardView({
  tasks,
  workflowColumns,
  projectsById,
  blockedTaskIds,
  selectedTaskId,
  showCompleted,
  onSelectTask,
  onToggleTask,
  onMoveTask,
}: {
  tasks: Task[];
  workflowColumns: WorkflowColumn[];
  projectsById: Map<string, Project>;
  blockedTaskIds: Set<string>;
  selectedTaskId: string | undefined;
  showCompleted: boolean;
  onSelectTask(taskId: string): void;
  onToggleTask(taskId: string): void;
  onMoveTask(taskId: string, workflowState: TaskWorkflowStateT): void;
}): JSX.Element {
  const store = getDocStore();
  const [newColumnName, setNewColumnName] = useState('');

  const columns = workflowColumns.map((column) => ({
    id: column.id,
    label: getTaskWorkflowStateLabel(column.id, workflowColumns),
    protected: column.protected,
    tasks: tasks
      .filter((task) => task.completedAt === undefined && task.workflowState === column.id)
      .sort(compareTasks),
  }));
  const completed = showCompleted
    ? tasks.filter((task) => task.completedAt !== undefined).sort(compareTasks)
    : [];

  return (
    <ScrollViewport className="min-h-0 flex-1" viewportClassName="h-full">
      <form
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          const created = store.createWorkflowColumn({ name: newColumnName });
          if (created) setNewColumnName('');
        }}
        className="flex flex-wrap items-center gap-2 border-b border-[color:var(--app-border)] px-4 py-3"
      >
        <input
          value={newColumnName}
          onChange={(event) => setNewColumnName(event.target.value)}
          placeholder="New workflow column"
          className="ui-input max-w-xs"
        />
        <button type="submit" className="ui-button ui-button-primary">
          Add column
        </button>
      </form>
      <div className="flex min-h-full gap-4 px-4 py-4">
        {columns.map((column, index) => (
          <BoardColumn
            key={column.id}
            columnId={column.id}
            title={column.label}
            testId={`board-column-${column.id}`}
            tasks={column.tasks}
            canMoveLeft={index > 0}
            canMoveRight={index < columns.length - 1}
            canDelete={!column.protected}
            selectedTaskId={selectedTaskId}
            projectsById={projectsById}
            blockedTaskIds={blockedTaskIds}
            onRename={(name) => store.renameWorkflowColumn(column.id, name)}
            onMoveLeft={() =>
              store.reorderWorkflowColumns(moveItem(workflowColumns, index, index - 1))
            }
            onMoveRight={() =>
              store.reorderWorkflowColumns(moveItem(workflowColumns, index, index + 1))
            }
            onDelete={() => {
              if (!window.confirm(`Delete "${column.label}" and move its tasks to Backlog?`)) {
                return;
              }
              store.removeWorkflowColumn(column.id, 'backlog');
            }}
            onSelectTask={onSelectTask}
            onToggleTask={onToggleTask}
            onMoveTask={(taskId) => onMoveTask(taskId, column.id)}
          />
        ))}
        {showCompleted && (
          <BoardColumn
            title="Done"
            testId="board-column-done"
            tasks={completed}
            selectedTaskId={selectedTaskId}
            projectsById={projectsById}
            blockedTaskIds={blockedTaskIds}
            onSelectTask={onSelectTask}
            onToggleTask={onToggleTask}
          />
        )}
      </div>
    </ScrollViewport>
  );
}

function BoardColumn({
  columnId,
  title,
  testId,
  tasks,
  canMoveLeft,
  canMoveRight,
  canDelete,
  selectedTaskId,
  projectsById,
  blockedTaskIds,
  onRename,
  onMoveLeft,
  onMoveRight,
  onDelete,
  onSelectTask,
  onToggleTask,
  onMoveTask,
}: {
  columnId?: string;
  title: string;
  testId: string;
  tasks: Task[];
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  canDelete?: boolean;
  selectedTaskId: string | undefined;
  projectsById: Map<string, Project>;
  blockedTaskIds: Set<string>;
  onRename?(name: string): void;
  onMoveLeft?(): void;
  onMoveRight?(): void;
  onDelete?(): void;
  onSelectTask(taskId: string): void;
  onToggleTask(taskId: string): void;
  onMoveTask?(taskId: string): void;
}): JSX.Element {
  return (
    <section
      data-testid={testId}
      onDragOver={(event) => {
        if (!onMoveTask) return;
        event.preventDefault();
      }}
      onDrop={(event) => {
        if (!onMoveTask) return;
        const taskId =
          event.dataTransfer.getData(DRAG_MIME) || event.dataTransfer.getData('text/plain');
        if (!taskId) return;
        event.preventDefault();
        onMoveTask(taskId);
      }}
      className="surface-panel-muted flex min-h-[24rem] w-[18rem] shrink-0 flex-col p-3"
    >
      <div className="mb-3 flex items-center justify-between gap-2 px-1">
        {onRename && columnId ? (
          <input
            aria-label={`${title} column name`}
            defaultValue={title}
            onBlur={(event) => onRename(event.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[color:var(--app-text)] outline-none"
          />
        ) : (
          <h3 className="text-sm font-semibold text-[color:var(--app-text)]">{title}</h3>
        )}
        <span className="ui-chip px-2 py-0.5 text-[11px]">{tasks.length}</span>
      </div>
      {(onMoveLeft || onMoveRight || onDelete) && (
        <div className="mb-3 flex gap-2 px-1">
          <button
            type="button"
            aria-label={`Move ${title} left`}
            disabled={!canMoveLeft}
            onClick={onMoveLeft}
            className="ui-button ui-button-subtle h-8 w-8 p-0"
          >
            ←
          </button>
          <button
            type="button"
            aria-label={`Move ${title} right`}
            disabled={!canMoveRight}
            onClick={onMoveRight}
            className="ui-button ui-button-subtle h-8 w-8 p-0"
          >
            →
          </button>
          <button
            type="button"
            aria-label={`Delete ${title}`}
            disabled={!canDelete}
            onClick={onDelete}
            className="ui-button ui-button-subtle h-8 w-8 p-0"
          >
            ×
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {tasks.length === 0 ? (
          <div className="surface-empty flex-1 p-4 text-xs">
            Drop a task here or move it from the inspector.
          </div>
        ) : (
          tasks.map((task) => (
            <BoardCard
              key={task.id}
              task={task}
              selected={task.id === selectedTaskId}
              blocked={blockedTaskIds.has(task.id)}
              projectName={projectsById.get(task.projectId)?.name ?? 'Project'}
              onSelect={() => onSelectTask(task.id)}
              onToggle={() => onToggleTask(task.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function moveItem(columns: WorkflowColumn[], from: number, to: number): string[] {
  const next = [...columns];
  const [item] = next.splice(from, 1);
  if (!item) return columns.map((column) => column.id);
  next.splice(to, 0, item);
  return next.map((column) => column.id);
}

function BoardCard({
  task,
  selected,
  blocked,
  projectName,
  onSelect,
  onToggle,
}: {
  task: Task;
  selected: boolean;
  blocked: boolean;
  projectName: string;
  onSelect(): void;
  onToggle(): void;
}): JSX.Element {
  return (
    <article
      draggable
      data-testid="task-board-card"
      data-task-id={task.id}
      data-selected={selected || undefined}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(DRAG_MIME, task.id);
        event.dataTransfer.setData('text/plain', task.id);
      }}
      className={`surface-panel cursor-grab p-3 active:cursor-grabbing ${
        selected ? 'ring-2 ring-[rgba(185,108,45,0.28)]' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          aria-label={task.completedAt !== undefined ? 'Reopen task' : 'Complete task'}
          onClick={onToggle}
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[10px] ${
            task.completedAt !== undefined
              ? 'border-[color:var(--app-success)] bg-[color:var(--app-success-soft)] text-[color:var(--app-success)]'
              : 'border-[color:var(--app-border-strong)] bg-white text-[color:var(--app-text-soft)]'
          }`}
        >
          {task.completedAt !== undefined ? '✓' : ''}
        </button>
        <button type="button" onClick={onSelect} className="min-w-0 flex-1 text-left">
          <p className="text-sm font-medium text-[color:var(--app-text)]">{task.title}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {task.priority > 0 && <span className="ui-chip ui-chip-accent">P{task.priority}</span>}
            <span className="ui-chip">{projectName}</span>
            {blocked && <span className="ui-chip ui-chip-danger">Blocked</span>}
            {task.krId && <span className="ui-chip ui-chip-info">KR linked</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-[color:var(--app-text-muted)]">
            {task.scheduledAt !== undefined && (
              <span>Scheduled {formatTaskDate(task.scheduledAt, task.scheduledAtHasTime)}</span>
            )}
            {task.scheduledAt === undefined && task.dueAt !== undefined && (
              <span>Due {formatTaskDate(task.dueAt, task.dueAtHasTime)}</span>
            )}
            {task.scheduledAt !== undefined && task.dueAt !== undefined && (
              <span>Due {formatTaskDate(task.dueAt, task.dueAtHasTime)}</span>
            )}
            {task.reminderAt !== undefined && (
              <span>Remind {formatShortDate(task.reminderAt)}</span>
            )}
          </div>
        </button>
      </div>
    </article>
  );
}
